import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Share2, Users, Gift, Zap, ShieldCheck, Target, TrendingUp, Search, Star, MessageCircle, Heart, Trophy } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Criar uma Estratégia de Indicação para IPTV", href: "/blog/como-criar-estrategia-indicacao-iptv" }
  ];

  const sections = [
    {
      title: "Indicação: O Marketing Mais Barato e Poderoso de 2026",
      content: (
        <div className="prose prose-invert max-w-none space-y-6 text-lg text-muted-foreground">
          <p>
            No mercado de IPTV em 2026, o custo de aquisição de um cliente (CAC) pode ser alto se você depender apenas de anúncios. No entanto, a <strong>estratégia de indicação</strong> (MGM - Member Get Member) permite que você cresça sua base de forma exponencial e quase gratuita. Um cliente que chega por indicação tem 4x mais chances de fechar o plano anual e 5x mais confiança na sua marca.
          </p>
          <p>
            Para que o "boca a boca" funcione, o produto precisa ser impecável. Como parceiro <strong>P2 PRO</strong>, você entrega o sinal mais estável do mercado e aplicativos luxuosos (One X). Isso faz com que seus clientes tenham <strong>orgulho de indicar</strong> o serviço para amigos e familiares, sabendo que não estarão indicando algo que trava ou é feio.
          </p>
          <div className="bg-electric/10 border-l-4 border-electric p-8 my-10 rounded-r-3xl relative overflow-hidden">
            <h4 className="text-white font-black mb-2 uppercase italic text-xl">Visão Master</h4>
            <p className="italic text-white/90">
              "Um cliente satisfeito é um vendedor que você não paga salário. Dê a ele o incentivo certo e ele trará o lucro para você."
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Formatos de Incentivo para sua Revenda",
      type: "grid" as const,
      content: (
        <>
          {[
            { t: "Mês de Bônus", d: "A cada indicação que fechar um plano, quem indicou ganha 30 dias grátis na assinatura atual.", i: <Gift /> },
            { t: "Desconto Progressivo", d: "Traga 3 amigos e pague metade do valor no seu próximo plano trimestral ou anual.", i: <TrendingUp /> },
            { t: "Grupo VIP de Indicações", d: "Crie um grupo onde os maiores indicadores recebem acesso antecipado a novidades e sorteios.", i: <Trophy /> },
            { t: "Cashback Master", d: "Devolva uma parte do valor da primeira mensalidade do indicado como crédito para quem indicou.", i: <Zap /> },
            { t: "Link de Indicação Personalizado", d: "Facilite o trabalho do seu cliente fornecendo um link que já cai direto no seu WhatsApp.", i: <Share2 /> },
            { t: "Atendimento Prioritário", d: "Indicadores de elite ganham um canal de suporte ainda mais rápido e vips.", i: <Heart /> }
          ].map((item, i) => (
            <div key={i} className="p-8 rounded-[2.5rem] bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-electric/10 text-electric flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                {item.i}
              </div>
              <h3 className="text-2xl font-black tracking-tighter mb-4 italic uppercase">{item.t}</h3>
              <p className="text-muted-foreground leading-relaxed">{item.d}</p>
            </div>
          ))}
        </>
      )
    },
    {
      title: "Como Implementar o Programa em 4 Passos",
      content: (
        <div className="prose prose-invert max-w-none space-y-6">
          <p className="text-lg text-muted-foreground">
            Não espere a indicação acontecer por sorte. Utilize o <strong>painel P2 PRO</strong> para gerenciar os bônus que você prometeu. Comunique seu programa de indicações em todos os pontos de contato: na mensagem de boas-vindas, nos Status do WhatsApp e no final de cada renovação. Se o cliente está feliz com o sinal 4K, ele é um forte candidato a ser um promotor da sua marca.
          </p>
          <div className="p-10 rounded-[3rem] bg-white/[0.02] border border-white/5 my-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-electric/5 blur-[80px] rounded-full" />
            <h4 className="text-2xl font-black mb-6 italic uppercase tracking-tighter flex items-center gap-3">
              <ShieldCheck className="text-electric w-8 h-8" /> Checklist da Indicação de Elite
            </h4>
            <ul className="space-y-4">
              <li className="flex gap-4 items-start">
                <Target className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Regras Claras:</span> O bônus só vale se o indicado pagar a primeira mensalidade. Transparência gera confiança.</p>
              </li>
              <li className="flex gap-4 items-start">
                <MessageCircle className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Lembrete Periódico:</span> Uma vez por mês, mande uma arte bonita no privado dos clientes lembrando do programa.</p>
              </li>
              <li className="flex gap-4 items-start">
                <Users className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Agradecimento Público:</span> Elogie e agradeça no grupo de clientes quem fez uma indicação bem-sucedida.</p>
              </li>
            </ul>
          </div>
          <p className="text-lg text-muted-foreground">
            Uma estratégia de indicação bem executada transforma sua revenda de IPTV em um <strong>organismo vivo</strong> que cresce sozinho. Ao entregar a qualidade inquestionável da <strong>P2 PRO</strong>, você garante que o "boca a boca" seja sempre positivo, gerando um faturamento recorrente sólido e duradouro.
          </p>
        </div>
      )
    }
  ];

  return (
    <SEOPage
      category="Dicas Como Revender Iptv"
      title="Como Criar uma Estratégia de Indicação para IPTV: Guia 2026"
      subtitle="Transforme seus clientes em vendedores. Aprenda a criar programas de incentivo que fazem sua base de assinantes crescer de forma orgânica e acelerada."
      heroImage="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&q=80&w=1200"
      introParagraphs={[
        "No mercado de streaming, nada supera a recomendação de um amigo. Em 2026, com a saturação de anúncios digitais, as pessoas confiam muito mais em quem elas conhecem. Se você não tem uma estratégia de indicação ativa, está deixando metade do seu lucro potencial na mesa.",
        "Neste artigo, vamos te mostrar como criar um programa de indicações 'ganha-ganha', onde seu cliente se sente motivado a te indicar e o novo assinante chega com a confiança lá no alto, facilitando o fechamento imediato.",
        "Com o suporte da P2 PRO, você oferece o melhor produto do mercado – com apps de design cinematográfico e sinal anti-travamento – o que é o combustível perfeito para que seus clientes queiram espalhar a novidade para todo o seu círculo social."
      ]}
      benefits={[
        { title: "Custo de Aquisição Zero", description: "O novo cliente chega até você sem que você precise gastar um centavo em marketing pago." },
        { title: "Alta Taxa de Conversão", description: "Leads indicados já vêm 'pré-vendidos' pela confiança que têm em quem indicou." },
        { title: "Fidelização em Dobro", description: "O cliente que indica se sente parte do negócio e tende a renovar por muito mais tempo." },
        { title: "Crescimento Exponencial", description: "Imagine se cada um dos seus 100 clientes trouxer apenas 1 amigo? Sua base dobra de tamanho instantaneamente." }
      ]}
      howItWorks={[
        { title: "Criação do Incentivo", description: "Defina qual benefício seu cliente terá ao trazer um novo assinante pagante." },
        { title: "Comunicação Eficiente", description: "Espalhe a novidade do seu programa de indicações em todos os seus canais de atendimento." },
        { title: "Entrega do Benefício", description: "Seja rápido e honesto ao entregar o bônus prometido para quem indicou." },
        { title: "Ciclo de Repetição", description: "Transforme o novo cliente indicado em um novo indicador, criando um ciclo de crescimento infinito." }
      ]}
      diferenciais={[
        { title: "Qualidade P2P Elite", description: "O orgulho de indicar um serviço que não trava e que vai deixar o amigo satisfeito." },
        { title: "Design White Label", description: "Apps luxuosos que valorizam a marca de quem indica e encantam quem recebe a indicação." },
        { title: "Suporte Master P2 PRO", description: "A segurança de que toda indicação será bem atendida pela nossa infraestrutura de ponta." }
      ]}
      faq={[
        { question: "Qual o melhor bônus para dar?", answer: "Tempo de tela grátis (ex: 15 ou 30 dias) é o que mais motiva os clientes de IPTV." },
        { question: "Como saber quem indicou quem?", answer: "Peça para o novo cliente dizer quem o indicou no primeiro contato ou use links parametrizados." },
        { question: "Posso dar dinheiro em vez de bônus?", answer: "Pode, mas tempo de tela tem um custo menor para você e mantém o cliente engajado com o seu serviço." },
        { question: "O que fazer se a indicação não fechar o plano?", answer: "Agradeça ao indicador mesmo assim e incentive-o a tentar com outra pessoa. O esforço deve ser valorizado." },
        { question: "Como evitar fraudes no programa?", answer: "O bônus só deve ser liberado após a confirmação do pagamento integral da primeira mensalidade do novo cliente." },
        { question: "Devo limitar o número de indicações?", answer: "De forma alguma! Incentive seus melhores clientes a terem 'assinatura eterna' através das indicações constantes." }
      ]}
      breadcrumbs={breadcrumbs}
      sections={sections}
      ctaTitle="🚀 Faça sua Revenda Crescer com o Poder da Indicação"
      ctaDescription="Entregue o melhor produto P2 PRO e deixe seus clientes venderem por você. Seja um Master de elite agora."
      ctaButtonText="Ser um Revendedor Master"
    />
  );
};

export const Route = createFileRoute("/blog/como-criar-estrategia-indicacao-iptv")({
  head: () => ({
    meta: [
      { title: "Como Criar uma Estratégia de Indicação para IPTV: Guia 2026 | P2 PRO" },
      { name: "description", content: "Aprenda a transformar clientes em promotores da sua revenda de IPTV. Guia completo sobre programas de indicação e marketing boca a boca." },
      { name: "keywords", content: "indicação iptv, member get member streaming, marketing boca a boca, como conseguir clientes orgânicos" }
    ],
  }),
  component: ArticlePage,
});